import { useState } from "react";
import moment from "moment";
import * as _ from "lodash";
import { Datum, HeatmapConfig } from "@ant-design/charts";
import { useDeepCompareEffect, useDeepCompareMemo } from "use-deep-compare";

import { ChartDataPointXAxisType, DataPointsMeta, HeatmapData, HeatmapMeta } from "@ctra/api";
import { TS, Units } from "@ctra/utils";

import { useChartContext, useRangeFilter, useSeriesColor } from "../../../providers";
import { useColorGradient, useCommonConfig } from "../../../hooks";
import "../Tooltip.css";

/**
 * Hook that merges heatmap config with the common config
 * The common config coming from the main app overrides the preset config
 * @returns
 */
export const useHeatmapConfig = (): { config: HeatmapConfig } => {
  const { commonConfig } = useCommonConfig();

  const {
    api: { getColor }
  } = useSeriesColor();

  const {
    values: { entities, data: d, meta: m, config: contextConfig }
  } = useChartContext<HeatmapData>();

  const chartID = _.get(entities, [0, "id"]);
  const inputData = _.get(d, [chartID], []);
  const meta = _.get(m, [chartID], {} as HeatmapMeta);

  const [data, setData] = useState(inputData);

  const { seriesType, seriesField, axis } = _.defaultTo(meta, {
    seriesType: void 0,
    seriesField: void 0,
    axis: {} as DataPointsMeta["axis"]
  });

  const colorGradient = useColorGradient(getColor(seriesType, seriesField));

  const {
    api: { updateRange, filterByRange }
  } = useRangeFilter();

  useDeepCompareEffect(() => {
    const range = updateRange(inputData);
    const newData = filterByRange(inputData, range);

    setData(newData);
  }, [inputData]);

  /**
   * Heatmap chart config
   * @type {{color: Array<string>, colorField: string, data: unknown, meta: {x: {type: string}}, tooltip: {title: (title, {x}: {x: any}) => string, fields: string[], shared: boolean, formatter: (datum: Datum) => {name: string, value: string}}, xAxis: {grid: null, label: {autoRotate: boolean, formatter: (value: string) => string | undefined}, line: {style: {lineWidth: number, stroke: string}}, tickLine: {alignTick: boolean, length: number, style: {lineWidth: number, stroke: string}}}, yAxis: {grid: null, line: {style: {lineWidth: number, stroke: string}}, tickLine: {length: number, style: {lineWidth: number, stroke: string}}}, xField: string, yField: string}}
   */
  const config: HeatmapConfig = useDeepCompareMemo(() => {
    return {
      color: colorGradient,
      colorField: "z",
      data,
      meta: {
        x: {
          type: "cat"
        }
      },
      tooltip: {
        title: (title, { x }) => TS.asMoment(x).format("LL"),
        fields: ["x", "y", "z"],
        shared: false,
        formatter: (datum: Datum) => ({
          name: `${_.padStart(datum.y, 2, "0")}:00`,
          value: Units.format(datum.z, axis?.y.title || "")
        })
      },
      xAxis: {
        grid: null,
        label: {
          autoRotate: true,
          formatter: (value: string) => {
            const [key] = _.isString(value) ? value.split("T") : [value];
            const isoWeekDay = moment(value).second();

            return _.isEqual(meta.xType, ChartDataPointXAxisType.weekDay)
              ? moment().isoWeekday(isoWeekDay).format("dddd")
              : axis?.x.labels?.short[key];
          }
        },
        line: {
          style: {
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        tickLine: {
          alignTick: true,
          length: 5,
          style: {
            lineWidth: 1,
            stroke: "#000000"
          }
        }
      },
      yAxis: {
        grid: null,
        line: {
          style: {
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        tickLine: {
          length: 5,
          style: {
            lineWidth: 1,
            stroke: "#000000"
          }
        }
      },
      xField: "x",
      yField: "y"
    };
  }, [colorGradient, data]);

  return {
    config: _.mergeWith({}, commonConfig, config, contextConfig, (objValue, srcValue) => {
      if (_.isFunction(objValue) && _.isFunction(srcValue)) {
        return _.over([objValue, srcValue]);
      }
    })
  };
};
